import { computed, ref, shallowRef, onBeforeUnmount } from "vue";
import { goSolidApi } from "@/composables/useGoSolidApi";
import { downloadFromUrl, downloadFromBlob } from "@/utils/downloadUtils";

const DEFAULT_POLLING_INTERVAL_MS = 3000;
const MAX_CONSECUTIVE_POLL_ERRORS = 5;

export type AsyncTaskState =
  "idle" | "starting" | "processing" | "success" | "failed";

/**
 * @property progress - The meta a task last reported with `update_state(state="PROGRESS")`; sent
 *   only while `status` is "PROGRESS".
 */
export interface AsyncTaskStatusResponse<TResult, TProgress = unknown> {
  task_id: string;
  status: string;
  ready: boolean;
  result?: TResult;
  progress?: TProgress;
  error?: string;
}

export interface AsyncTaskStartResponse {
  task_id: string;
  status: "processing";
}

export interface UseAsyncTaskOptions {
  pollingIntervalMs?: number;
}

export function useAsyncTask<TResult, TProgress = unknown>(
  options: UseAsyncTaskOptions = {}
) {
  const pollingIntervalMs =
    options.pollingIntervalMs ?? DEFAULT_POLLING_INTERVAL_MS;

  const state = ref<AsyncTaskState>("idle");
  const taskId = ref<string | null>(null);
  const result = ref<TResult | null>(null);
  // Latest progress the running task reported, or null until it reports any.
  const progress = shallowRef<TProgress | null>(null);
  const errorMessage = ref<string | null>(null);
  // The raw failure, when there is one to keep: an axios error carries the backend's error code,
  // which a message string cannot, and which is what support needs to find the server-side log.
  // shallowRef so the foreign error object is handed back as-is, not wrapped in a deep proxy.
  const error = shallowRef<unknown>(null);
  let pollingInterval: number | null = null;
  let consecutivePollErrors = 0;
  // Stamps each execute()/reset(). A response that awaited across a newer execute or a reset
  // belongs to a superseded run: acting on it would overwrite the current run's state or stop
  // its polling, so every continuation below re-checks its stamp after awaiting.
  let generation = 0;

  const isRunning = computed(
    () => state.value === "starting" || state.value === "processing"
  );

  const stopPolling = () => {
    if (pollingInterval !== null) {
      clearInterval(pollingInterval);
      pollingInterval = null;
    }
  };

  const pollTaskStatus = async () => {
    if (!taskId.value) return;
    const pollGeneration = generation;

    try {
      const response = await goSolidApi.get<
        AsyncTaskStatusResponse<TResult, TProgress>
      >(`/api/async-tasks/${taskId.value}/status`);
      if (pollGeneration !== generation) return;
      const data = response.data;
      consecutivePollErrors = 0;

      if (data.status === "PROGRESS" && data.progress !== undefined) {
        progress.value = data.progress;
      }

      if (data.ready) {
        stopPolling();
        if (data.error) {
          state.value = "failed";
          errorMessage.value = data.error;
        } else if (data.result !== undefined) {
          state.value = "success";
          result.value = data.result;
        } else {
          state.value = "failed";
          errorMessage.value = "Task ended without a result";
        }
      }
    } catch (error) {
      if (pollGeneration !== generation) return;
      consecutivePollErrors += 1;
      console.error("Error polling task status:", error);
      if (consecutivePollErrors >= MAX_CONSECUTIVE_POLL_ERRORS) {
        stopPolling();
        state.value = "failed";
        errorMessage.value = "Lost connection to task status endpoint";
      }
    }
  };

  const startPolling = () => {
    pollingInterval = window.setInterval(pollTaskStatus, pollingIntervalMs);
  };

  /**
   * Start the task and poll it to completion.
   *
   * Settles exactly once, into `state`: a failed start is reported the same way a failed task is
   * ("failed", with `error` and `errorMessage` set) and is never also rethrown. Doing both would
   * leave every caller with two ways to hear about one failure — and reporting from both of them
   * relies on Vue flushing the `state` watcher before the rejection is delivered, which it does not.
   *
   * Calling execute again supersedes the previous run entirely: its polling stops and any of its
   * responses still in flight are discarded, so a restarted task can't have its state overwritten
   * or its polling stopped by the run it replaced.
   */
  const execute = async <TArgs extends unknown[]>(
    startTaskFn: (...args: TArgs) => Promise<AsyncTaskStartResponse>,
    ...args: TArgs
  ) => {
    const runGeneration = beginRun();
    state.value = "starting";

    try {
      const response = await startTaskFn(...args);
      if (runGeneration !== generation) return;
      taskId.value = response.task_id;
      state.value = "processing";
      startPolling();
    } catch (startError) {
      if (runGeneration !== generation) return;
      error.value = startError;
      errorMessage.value = "Failed to start task";
      state.value = "failed";
    }
  };

  /**
   * Resume polling a task started earlier (e.g. before a page reload). Supersedes any current run
   * exactly like `execute` does. Unlike a freshly started task, this one may already be finished,
   * so the first poll goes out at once rather than after the first interval.
   */
  const attach = (existingTaskId: string) => {
    beginRun();
    taskId.value = existingTaskId;
    state.value = "processing";
    void pollTaskStatus();
    startPolling();
  };

  /**
   * Ask the backend to stop the running task. Polling continues until the task itself reports
   * ready, so the state settles into "failed" through the normal path. If the request fails, the
   * run settles into "failed" right away with the request error attached.
   */
  const cancel = async () => {
    if (!taskId.value || !isRunning.value) return;
    const runGeneration = generation;
    try {
      await goSolidApi.post(`/api/async-tasks/${taskId.value}/cancel`);
    } catch (cancelError) {
      if (runGeneration !== generation) return;
      stopPolling();
      error.value = cancelError;
      errorMessage.value = "Failed to cancel task";
      state.value = "failed";
    }
  };

  function beginRun() {
    stopPolling();
    result.value = null;
    progress.value = null;
    errorMessage.value = null;
    error.value = null;
    consecutivePollErrors = 0;
    return ++generation;
  }

  const downloadResult = async () => {
    if (!taskId.value) return;
    const runGeneration = generation;

    try {
      const response = await goSolidApi.get(
        `/api/async-tasks/${taskId.value}/download`
      );
      if (runGeneration !== generation) return;
      const { download_url, filename, requires_auth } = response.data;
      // The backend tells us which storage mode it's in via `requires_auth`. S3 presigned URLs are
      // self-authenticating, so a plain anchor click works. Local-storage downloads (dev/test) point
      // back at our own Bearer-protected API, which an anchor navigation can't reach (it drops the
      // Authorization header) — fetch those through the authenticated API client as a blob instead.
      if (requires_auth) {
        const fileResponse = await goSolidApi.get(download_url, {
          responseType: "blob",
        });
        if (runGeneration !== generation) return;
        downloadFromBlob(fileResponse.data, filename);
      } else {
        downloadFromUrl(download_url, filename);
      }
    } catch {
      if (runGeneration !== generation) return;
      state.value = "failed";
      errorMessage.value = "Failed to download result";
    }
  };

  const reset = () => {
    stopPolling();
    generation += 1;
    state.value = "idle";
    taskId.value = null;
    result.value = null;
    progress.value = null;
    errorMessage.value = null;
    error.value = null;
    consecutivePollErrors = 0;
  };

  onBeforeUnmount(() => stopPolling());

  return {
    state,
    taskId,
    isRunning,
    downloadResult,
    result,
    progress,
    error,
    errorMessage,
    execute,
    attach,
    cancel,
    reset,
  };
}
